import { useGetItemsByBusinessPartnerId } from '@/api/business-partner/business-partner.hooks';
import { useUpsertCartItem } from '@/api/carts/carts.hooks';
import { useItemFacets } from '@/api/items/items.hooks';
import { Item } from '@/api/items/items.types';
import { ITEM_GROUP_ORDER } from '@/constants/item-group-order';
import { useCatalogFilterStore } from '@/stores/catalog-filter-store';
import { formatMoneyString } from '@utils/format-money-string';
import { smartNameCompare } from '@/utils/smart-name-compare';
import Button from '@components/base/button.component';
import {
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AddShoppingCart, ArrowForward } from '@mui/icons-material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const PLACEHOLDER_SVG = `data:image/svg+xml,${encodeURIComponent(`<svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="120" height="120" rx="8" fill="#f0f0f0"/><path d="M36 78l15-20 10.5 13.5 15-19.5L93 78" stroke="#c0c0c0" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/><circle cx="48" cy="48" r="6" fill="#c0c0c0"/></svg>`)}`;


const STOCK_COLORS = {
  available: '#43a047',
  ordered: '#ef6c00',
  unavailable: '#e53935',
} as const;

function sortByNameGrouped(items: Item[]): Item[] {
  return [...items].sort((a, b) => {
    const aIdx = a.itemGroup ? ITEM_GROUP_ORDER.indexOf(a.itemGroup) : -1;
    const bIdx = b.itemGroup ? ITEM_GROUP_ORDER.indexOf(b.itemGroup) : -1;
    const aOrder = aIdx === -1 ? ITEM_GROUP_ORDER.length : aIdx;
    const bOrder = bIdx === -1 ? ITEM_GROUP_ORDER.length : bIdx;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return smartNameCompare(a.name, b.name);
  });
}

function ArticleCard({ item }: { item: Item }) {
  const { t } = useTranslation('customer');
  const addToCart = useUpsertCartItem();

  const availableQuantity = Math.max(
    item.quantityOnStock - item.quantityOrderedByCustomers,
    0,
  );
  const orderedQuantity = item.quantityOrderedFromVendors;

  let stockColor: string;
  let stockLabel: string;

  if (availableQuantity === 0 && orderedQuantity === 0) {
    stockColor = STOCK_COLORS.unavailable;
    stockLabel = t('items.stock.notAvailable');
  } else if (availableQuantity === 0 && orderedQuantity > 0) {
    stockColor = STOCK_COLORS.ordered;
    stockLabel = t('items.stock.ordered');
  } else {
    stockColor = STOCK_COLORS.available;
    if (availableQuantity < 20) stockLabel = `${availableQuantity}`;
    else if (availableQuantity < 50) stockLabel = '> 20';
    else if (availableQuantity < 100) stockLabel = '> 50';
    else if (availableQuantity < 200) stockLabel = '> 100';
    else if (availableQuantity < 500) stockLabel = '> 200';
    else stockLabel = '> 500';
  }

  return (
    <Card
      variant="outlined"
      sx={{
        width: 200,
        minWidth: 200,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        borderLeftWidth: 2,
        borderLeftColor: `${stockColor}55`,
        borderRadius: 2.5,
        transition: 'box-shadow 0.2s, transform 0.2s',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(2, 52, 76, 0.10)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      {/* Image */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 100,
          bgcolor: '#f7f8f9',
          px: 1.5,
          py: 1,
        }}
      >
        <img
          src={item.imageUrl || PLACEHOLDER_SVG}
          alt=""
          loading="lazy"
          style={{ maxWidth: '100%', maxHeight: 80, objectFit: 'contain' }}
        />
      </Box>

      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          p: 1.25,
          '&:last-child': { pb: 1.25 },
        }}
      >
        {/* Name */}
        <Tooltip title={item.name} enterDelay={400}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              lineHeight: 1.3,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: '2.1em',
              mb: 0.25,
            }}
          >
            {item.name}
          </Typography>
        </Tooltip>

        {/* Article Number */}
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{
            fontFamily: '"Roboto Mono", monospace',
            fontSize: '0.6rem',
            letterSpacing: '0.02em',
            mb: 0.5,
          }}
        >
          {item.id}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {/* Stock */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 0.75,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: stockColor,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="caption"
            sx={{ fontSize: '0.6rem', color: 'text.secondary', fontWeight: 500 }}
          >
            {stockLabel}
          </Typography>
        </Box>

        <Divider sx={{ mb: 0.75 }} />

        {/* Price + Cart */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              fontSize: '0.85rem',
              color: 'primary.main',
              letterSpacing: '-0.01em',
            }}
          >
            {item.itemPrice > 0
              ? formatMoneyString(item.itemPrice)
              : 'Auf Anfrage'}
          </Typography>
          <Tooltip title="In den Warenkorb">
            <IconButton
              size="small"
              onClick={() =>
                addToCart.mutate({
                  cartItems: [{ itemId: item.id, quantity: 1 }],
                })
              }
              sx={{
                color: 'primary.main',
                bgcolor: 'rgba(2, 52, 76, 0.06)',
                width: 26,
                height: 26,
                transition: 'all 0.15s',
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'white',
                },
              }}
            >
              <AddShoppingCart sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
}

const CATEGORY_ROWS = [
  { label: 'Smartphones', group: 'Smartphone' },
  { label: 'Tablets', group: 'Tablet' },
  { label: 'Notebooks', group: 'Notebook' },
] as const;

const FADE_WIDTH = 48;

function ArticleRow({
  label,
  items,
  isPending,
  onViewAll,
}: {
  label: string;
  items: Item[];
  isPending: boolean;
  onViewAll: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const updateFades = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setShowLeftFade(scrollLeft > 10);
    setShowRightFade(scrollLeft + clientWidth < scrollWidth - 10);
  }, []);

  useEffect(() => {
    updateFades();
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(updateFades);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateFades, items]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, fontSize: '0.85rem' }}
        >
          {label}
        </Typography>
        <Button
          size="small"
          endIcon={<ArrowForward sx={{ fontSize: '14px !important' }} />}
          onClick={onViewAll}
          sx={{ fontSize: '0.7rem' }}
        >
          Alle {label} anzeigen
        </Button>
      </Box>

      <Box sx={{ position: 'relative', mx: -0.5 }}>
        {/* Left fade */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: FADE_WIDTH,
            background: `linear-gradient(to right, #E9E9E9, ${alpha('#E9E9E9', 0)})`,
            zIndex: 1,
            pointerEvents: 'none',
            opacity: showLeftFade ? 1 : 0,
            transition: 'opacity 0.25s',
          }}
        />
        {/* Right fade */}
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: FADE_WIDTH,
            background: `linear-gradient(to left, #E9E9E9, ${alpha('#E9E9E9', 0)})`,
            zIndex: 1,
            pointerEvents: 'none',
            opacity: showRightFade ? 1 : 0,
            transition: 'opacity 0.25s',
          }}
        />

        <Box
          ref={scrollRef}
          onScroll={updateFades}
          sx={{
            display: 'flex',
            gap: 1.5,
            overflowX: 'auto',
            pb: 1,
            px: 0.5,
            scrollSnapType: 'x mandatory',
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'grey.300',
              borderRadius: 2,
            },
          }}
        >
          {isPending
            ? [...Array(6)].map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  sx={{
                    minWidth: 200,
                    height: 280,
                    borderRadius: 2.5,
                    flexShrink: 0,
                  }}
                />
              ))
            : items.map((item) => (
                <Box
                  key={item.id}
                  sx={{ scrollSnapAlign: 'start', display: 'flex' }}
                >
                  <ArticleCard item={item} />
                </Box>
              ))}
        </Box>
      </Box>
    </Box>
  );
}

export function FeaturedArticlesSection() {
  const navigate = useNavigate();
  const { clearAll, setItemGroup } = useCatalogFilterStore();
  const { data: items, isPending } = useGetItemsByBusinessPartnerId();
  const { data: facets } = useItemFacets(
    undefined,
  );

  const itemsByGroup = useMemo(() => {
    if (!items) return {};
    const grouped: Record<string, Item[]> = {};
    for (const row of CATEGORY_ROWS) {
      const groupItems = items.filter((i) => i.itemGroup === row.group);
      grouped[row.group] = sortByNameGrouped(groupItems).slice(0, 12);
    }
    return grouped;
  }, [items]);

  const facetMap = useMemo(
    () => new Map(facets?.itemGroupFacets.map((f) => [f.name, f]) ?? []),
    [facets],
  );

  const handleViewAll = (group: string) => {
    const facet = facetMap.get(group);
    if (facet) {
      clearAll();
      setItemGroup(facet.id, facet.name);
      navigate('/bp/c/items');
    }
  };

  return (
    <Stack spacing={3}>
      {CATEGORY_ROWS.map((row) => (
        <ArticleRow
          key={row.group}
          label={row.label}
          items={itemsByGroup[row.group] ?? []}
          isPending={isPending}
          onViewAll={() => handleViewAll(row.group)}
        />
      ))}
    </Stack>
  );
}
